/**
 * Browser-compatible mutator for cognito-api-sdk.
 *
 * Drop-in replacement for mutator.ts that uses the native Fetch API
 * instead of axios + Node.js https.Agent. Safe to bundle in Office Add-ins,
 * web apps, and browser extensions — no Node.js built-ins required.
 *
 * Usage: pass --browser to copy-mutator.ts (or generate:browser script)
 * so this file is copied to the generated directory instead of mutator.ts.
 */

import type { AxiosRequestConfig } from 'axios';
// ATENÇÃO: este arquivo é sobrescrito por `npm run setup:sdk`. Este import e o spread de
// COGNITO_CLIENT_HEADERS abaixo são reinjetados pelo scripts/setup-sdk.ts — se você mexer
// aqui, mexa lá também, senão a identificação do add-in no SigNoz desaparece na próxima
// regeneração do SDK.
import { COGNITO_CLIENT_HEADERS } from '../../shared/client-identity';
import type { VaultApiResponse } from './shared/vault-api-response';

const DEFAULT_BASE_URL = 'https://api.cognitobrm.com';
const DEFAULT_STORAGE_KEY = 'authToken';

// ─── Public interfaces ───────────────────────────────────────────────────────

export interface CognitoApiInstanceOptions {
  baseURL?: string;
  apiKey?: string | null;
  persistAuthToken?: boolean;
  storageKey?: string;
  /** Ignored in browser mode — present only for API compatibility with mutator.ts. */
  axiosConfig?: AxiosRequestConfig;
}

/** Browser version — axiosInstance is always null (Fetch API is used instead). */
export interface CognitoApiContext {
  axiosInstance: null;
  getAuthToken(): string | null;
  setAuthToken(token: string | null): void;
  setBaseURL(url: string): void;
  getBaseURL(): string;
}

export interface CognitoApiScope {
  context: CognitoApiContext;
  setAuthToken(token: string | null): void;
  setBaseURL(url: string): void;
  getAuthToken(): string | null;
  getBaseURL(): string;
}

// ─── Internal context ────────────────────────────────────────────────────────

class BrowserApiContext implements CognitoApiContext {
  public readonly axiosInstance: null = null;
  private tokenOverride: string | null;
  private _baseURL: string;
  private readonly persistToken: boolean;
  private readonly storageKey: string;

  constructor(options: CognitoApiInstanceOptions = {}) {
    this._baseURL = options.baseURL ?? DEFAULT_BASE_URL;
    this.tokenOverride = options.apiKey ?? null;
    this.persistToken = options.persistAuthToken ?? false;
    this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
  }

  public getBaseURL(): string {
    return this._baseURL;
  }

  public setBaseURL(url: string): void {
    this._baseURL = url;
  }

  public setAuthToken(token: string | null): void {
    this.tokenOverride = token;

    if (!this.persistToken) return;

    try {
      const storage = this.getStorage();
      if (token) {
        storage?.setItem(this.storageKey, token);
      } else {
        storage?.removeItem(this.storageKey);
      }
    } catch {
      // Ignore storage errors in restrictive environments (e.g. Office shared runtime)
    }
  }

  public getAuthToken(): string | null {
    if (this.tokenOverride) return this.tokenOverride;

    if (this.persistToken) {
      try {
        const stored = this.getStorage()?.getItem(this.storageKey);
        if (stored) return stored;
      } catch {
        // Ignore
      }
    }

    return null;
  }

  private getStorage(): Storage | null {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const g = globalThis as any;
      return g?.window?.localStorage ?? g?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}

// ─── Scope factory ────────────────────────────────────────────────────────────

export function createApiScope(options?: CognitoApiInstanceOptions): CognitoApiScope {
  const context = new BrowserApiContext(options);
  return {
    context,
    setAuthToken: (token) => context.setAuthToken(token),
    setBaseURL: (url) => context.setBaseURL(url),
    getAuthToken: () => context.getAuthToken(),
    getBaseURL: () => context.getBaseURL(),
  };
}

const defaultScope = createApiScope({ persistAuthToken: true });
let activeContext: CognitoApiContext = defaultScope.context;

function resolveContext(): CognitoApiContext {
  return activeContext;
}

export function withApiContext<T>(context: CognitoApiContext, fn: () => T): T {
  const previous = activeContext;
  activeContext = context;
  try {
    return fn();
  } finally {
    activeContext = previous;
  }
}

/** Sets the auth token on the default (global) scope. */
export function setAuthToken(token: string | null): void {
  defaultScope.setAuthToken(token);
}

/** Sets the base URL on the default (global) scope. */
export function setBaseURL(url: string): void {
  defaultScope.setBaseURL(url);
}

export function initCognitoApi(options: CognitoApiInstanceOptions = {}): CognitoApiScope {
  if (options.baseURL) defaultScope.setBaseURL(options.baseURL);
  if (options.apiKey !== undefined) defaultScope.setAuthToken(options.apiKey ?? null);
  return defaultScope;
}

// ─── Response transformation (mirrors mutator.ts logic) ──────────────────────

interface ResultObject<T = unknown> {
  operationId?: string | null;
  code?: number;
  message?: string | null;
  status?: boolean;
  data?: T | null;
  errors?: unknown[] | null;
}

type ExtractDataFromResultObject<T> = T extends { data?: infer D }
  ? D extends undefined
    ? T
    : D
  : T;

function isResultObject(obj: unknown): obj is ResultObject<unknown> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    ('code' in obj || 'message' in obj || 'status' in obj)
  );
}

// ─── customInstance — the mutator called by orval-generated code ──────────────

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<VaultApiResponse<ExtractDataFromResultObject<T>>> => {
  // Context is captured synchronously — safe to use with async/await below
  const context = resolveContext();

  const mergedConfig: AxiosRequestConfig = { ...config, ...options };

  // Build full URL
  const base = mergedConfig.baseURL ?? context.getBaseURL();
  const urlPath = mergedConfig.url ?? '';
  const fullUrl = urlPath.startsWith('http') ? urlPath : `${base}${urlPath}`;

  // Append query params
  let finalUrl = fullUrl;
  if (mergedConfig.params && Object.keys(mergedConfig.params as object).length > 0) {
    finalUrl = `${fullUrl}?${new URLSearchParams(mergedConfig.params as Record<string, string>).toString()}`;
  }

  // Build headers
  const combinedHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    // Identifica o add-in no SigNoz (`cognito.client`); vem antes dos headers do chamador
    // para que uma chamada específica ainda possa sobrescrever, se precisar.
    ...COGNITO_CLIENT_HEADERS,
    ...((config.headers as Record<string, string>) ?? {}),
    ...((options?.headers as Record<string, string>) ?? {}),
  };

  const token = context.getAuthToken();
  if (token) {
    combinedHeaders['Authorization'] = `Bearer ${token}`;
  }

  const body = mergedConfig.data != null ? JSON.stringify(mergedConfig.data) : undefined;

  return fetch(finalUrl, {
    method: (mergedConfig.method ?? 'GET').toUpperCase(),
    headers: combinedHeaders,
    body,
  }).then(async (response) => {
    let rawData: T;
    try {
      rawData = (await response.json()) as T;
    } catch {
      // Non-JSON body (e.g. 401 HTML page from auth proxy)
      return {
        code: response.status,
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: false,
        data: null,
      } as VaultApiResponse<ExtractDataFromResultObject<T>>;
    }

    if (isResultObject(rawData)) {
      const errors = rawData.errors as Array<{ message?: string }> | null | undefined;
      const detailedMessage = (!rawData.status && errors?.length)
        ? (errors[0].message ?? rawData.message ?? '')
        : (rawData.message ?? '');
      return {
        code: rawData.code ?? (response.ok ? 200 : response.status),
        message: detailedMessage,
        status: rawData.status ?? response.ok,
        data: (rawData.data ?? null) as ExtractDataFromResultObject<T> | null,
      };
    }

    // Direct type response (e.g. TokenResponse — not wrapped in ResultObject)
    return {
      code: response.status,
      message: '',
      status: response.ok,
      data: rawData as ExtractDataFromResultObject<T>,
    };
  });
};

export default customInstance;
